import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import generalSettings from "@apis/generalSettings";
import CardHeader from "@commons/businesscardHeader";
import Fields from "@components/fields";
import Row from "@components/row";
import Wrapper from "@components/wrapper";
import Button from "@components/button";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import { Module } from "@apis/index";

const ModuleWisePercent = () => {
  const { data: modules } = useSelector((state: RootState) => state.modules);
  const [existing, setExisting] = useState<any | null>(null);
  const fetchModules = useCallback(async () => {
    await Module.list({ state: "modules" });
  }, []);
  const fetchBusinessRules = useCallback(async () => {
    try {
      const data = await generalSettings.getByKey({
        id: "module-wise-percentage",
        state: "modulesPercentage",
        showErr: false,
      });
      if (data) {
        setExisting(data);
      }
    } catch (err) {
      console.error("Failed to fetch business rules", err);
    }
  }, []);

  useEffect(() => {
    fetchBusinessRules();
    fetchModules();
  }, [fetchBusinessRules, fetchModules]);

  const handleSubmit = async (values: any) => {
    const payload: any = {
      key: "module-wise-percentage",
      infos: JSON.stringify(values),
    };

    if (!existing?.id) {
      payload.group = "business-settings";
    }

    try {
      if (existing?.id) {
        await generalSettings.update({
          id: existing.id,
          state: "modulesPercentage",
          data: payload,
        });
        toast.success("Business rules updated successfully!");
      } else {
        await generalSettings.create({
          state: "modulesPercentage",
          data: payload,
        });
        toast.success("Business rules created successfully!");
      }
      fetchBusinessRules();
    } catch (err) {
      console.error("Failed to save business rules", err);
      toast.error("Failed to save business rules");
    }
  };

  const formatLabel = (key: string) => {
    return key
      .replace("Percentage", "")
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const getInitialValues = () => {
    const initialValues: { [key: string]: { type: string; value: string } } =
      {};

    if (Array.isArray(modules)) {
      modules.forEach((module) => {
        const key = `${module.name}-${module.id}`;
        const storedValue = existing?.infos?.[key];

        if (storedValue && typeof storedValue === "object") {
          initialValues[key] = {
            type: storedValue.type || "percent",
            value: storedValue.value || "",
          };
        } else {
          // Backward compatibility for existing simple values
          initialValues[key] = {
            type: "percent",
            value: storedValue || "",
          };
        }
      });
    }

    return initialValues;
  };

  const typeOptions = [
    { label: "%", value: "percent" },
    { label: "Flat", value: "flat" },
  ];

  return (
    <Fragment>
      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <CardHeader title="Module Wise Commission" />
            <Wrapper>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", // Responsive grid
                  gap: "20px",
                }}
              >
                {modules.map((module) => {
                  const key = `${module.name}-${module.id}`;
                  const fieldLabel = formatLabel(module.name);
                  const fieldValue = values[key] || {
                    type: "percent",
                    value: "",
                  };

                  return (
                    <div
                      key={module.id}
                      style={{
                        background: "#fff",
                        padding: "20px",
                        borderRadius: "12px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                        border: "1px solid #e5e7eb",
                      }}
                    >
                      <label
                        style={{
                          display: "block",
                          marginBottom: "12px",
                          fontWeight: 600,
                          fontSize: "15px",
                          color: "#1f2937",
                        }}
                      >
                        {fieldLabel}
                      </label>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "stretch",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          overflow: "hidden",
                          height: "48px",
                        }}
                      >
                        <div
                          style={{
                            flex: "0 0 60px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f3f4f6",
                            borderRight: "1px solid #e5e7eb",
                            cursor: "pointer",
                            fontWeight: 600,
                            color:
                              fieldValue.type === "percent"
                                ? "#3b82f6"
                                : "#6b7280",
                            transition: "all 0.2s",
                          }}
                          onClick={() => {
                            const newType =
                              fieldValue.type === "percent"
                                ? "flat"
                                : "percent";
                            setFieldValue(key, {
                              ...fieldValue,
                              type: newType,
                            });
                          }}
                          title="Click to switch type"
                        >
                          {fieldValue.type === "percent" ? "%" : "Flat"}
                        </div>
                        <div style={{ flex: 1 }}>
                          <Fields.Input
                            name={`${key}.value`}
                            label=""
                            placeholder={
                              fieldValue.type === "percent" ? "10" : "50"
                            }
                            type="number"
                            styleInput="border: none; margin: 0; padding: 0 16px; height: 100%; width: 100%; font-size: 16px; outline: none;"
                            styleWrapper="margin: 0; padding: 0; border: none; height: 100%;"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <br />
              <Button
                type="submit"
                title={existing ? "Update" : "Save"}
                justify="end"
              />
            </Wrapper>
          </Form>
        )}
      </Formik>
    </Fragment>
  );
};

export default ModuleWisePercent;
